import { z } from 'zod'
import { Commitment, MeetingSummary, TaskFull } from './m2.ts'
import { patchOf } from './patch.ts'

/**
 * M4 – calendar, day plan and "Heute" (§16.1–16.2). Paths below /api/v1 unless noted.
 *
 * GET    /today?date=                     → TodayView (date defaults to today, Europe/Berlin)
 * POST   /plans/:date/generate            → DailyPlan  (draft; "Neu planen" when one exists)
 * PATCH  /plans/:date                     → DailyPlan  body UpdatePlan (moved/removed blocks)
 * POST   /plans/:date/accept              → AcceptResult (writes ◆ blocks to the calendar, if connected)
 * POST   /plans/:date/review              → DailyPlan  body EveningReview
 * GET    /planning/settings · PATCH       → PlanningSettings
 * GET    /calendar/accounts               → CalendarAccountInfo[]
 * GET    /calendar/google/connect         → 302 to Google (incremental calendar scope)
 * GET    /calendar/google/callback        → 302 back to /einstellungen (code exchange)
 * PATCH  /calendar/accounts/:id           → CalendarAccountInfo body UpdateCalendarAccount
 * DELETE /calendar/accounts/:id           → 204 (tokens and cache removed; calendar events stay untouched)
 * POST   /calendar/sync                   → { synced: number, replanned: ReplanOutcome[] }
 * GET    /calendar/events/:id             → CalendarEventView (invite details, attendees ↔ people, linked meetings)
 * POST   /calendar/events/:id/meeting-note → EventMeetingNoteCreated body CreateEventMeetingNote
 */

const id = z.uuid()
const isoDate = z.iso.date()
const isoDateTime = z.iso.datetime({ offset: true })
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)

/** Title prefix of brain blocks in the calendar (§16.1); together with extendedProperties.private.brain. */
export const BRAIN_BLOCK_PREFIX = '◆ '

export const CalendarAttendee = z.object({
  email: z.string(),
  name: z.string().nullable(),
  self: z.boolean(),
  organizer: z.boolean(),
  responseStatus: z.string().nullable(),
})
export type CalendarAttendee = z.infer<typeof CalendarAttendee>

export const CalendarEvent = z.object({
  /** id in calendar_events_cache */
  id,
  externalId: z.string(),
  calendarId: z.string(),
  title: z.string(),
  start: isoDateTime,
  end: isoDateTime,
  allDay: z.boolean(),
  attendees: z.array(CalendarAttendee),
  /** has attendees other than the user (§16.3 "externe Teilnehmer") */
  external: z.boolean(),
  location: z.string().nullable(),
  htmlLink: z.string().nullable(),
  isBrainBlock: z.boolean(),
  /** 'opaque' = busy, 'transparent' = free (brain blocks are tentative/free) */
  transparency: z.enum(['opaque', 'transparent']),
})
export type CalendarEvent = z.infer<typeof CalendarEvent>

/** Attendee of a calendar event, matched to a person (e-mail, name or alias – ADR 0011). */
export const EventAttendee = CalendarAttendee.extend({
  personId: id.nullable(),
  personName: z.string().nullable(),
})
export type EventAttendee = z.infer<typeof EventAttendee>

/** Event dialog on "Heute" (ADR 0016): invite text and call link are read from the calendar on demand. */
export const CalendarEventView = z.object({
  event: CalendarEvent,
  /** null without a connected calendar or when the provider could not be reached */
  description: z.string().nullable(),
  meetingUrl: z.string().nullable(),
  attendees: z.array(EventAttendee),
  /** meetings already linked to this event (meetings.calendar_event_id = event.externalId) */
  meetings: z.array(MeetingSummary),
  /** project of the most similar earlier meeting – default for a new meeting note / live session */
  suggestedContainerId: id.nullable(),
  /** prefilled context for a live session (title, time, attendees, invite text) */
  context: z.string(),
  /** earlier meetings with this title (short prep) */
  history: z.array(MeetingSummary),
})
export type CalendarEventView = z.infer<typeof CalendarEventView>

export const CreateEventMeetingNote = z.object({ containerId: id.nullable().default(null) })
export type CreateEventMeetingNote = z.infer<typeof CreateEventMeetingNote>

export const EventMeetingNoteCreated = z.object({
  meetingId: id,
  noteId: id,
  /** a meeting note for this event existed already – nothing new was created */
  existing: z.boolean(),
})
export type EventMeetingNoteCreated = z.infer<typeof EventMeetingNoteCreated>

export const PlanBlockKind = z.enum(['task', 'focus', 'prep', 'followup'])
export type PlanBlockKind = z.infer<typeof PlanBlockKind>

export const PlanBlock = z.object({
  id: z.string().min(1).max(64),
  kind: PlanBlockKind,
  title: z.string().max(500),
  start: isoDateTime,
  end: isoDateTime,
  taskId: id.nullable(),
  /** prep/follow-up: the calendar event (cache id) it belongs to */
  eventId: z.string().nullable(),
  containerId: id.nullable(),
  /** energy match of the slot (deep work window) */
  deep: z.boolean(),
  /** moved by hand – "Neu planen" keeps it where it is */
  pinned: z.boolean(),
  /** ◆ event in the calendar once accepted */
  calendarEventId: z.string().nullable(),
})
export type PlanBlock = z.infer<typeof PlanBlock>

export const PlanStatus = z.enum(['draft', 'accepted', 'replanned'])
export const ReviewItem = z.object({
  taskId: id,
  done: z.boolean(),
  /** "Verschieben auf": sets do_date */
  movedTo: isoDate.nullable(),
  /** "Was blockiert?" */
  blocked: z.string().max(2000),
  /** actual minutes (improves estimates per container) */
  actualMin: z.number().int().min(0).max(1440).nullable(),
})
export type ReviewItem = z.infer<typeof ReviewItem>

export const DailyPlan = z.object({
  id,
  date: isoDate,
  status: PlanStatus,
  blocks: z.array(PlanBlock),
  briefingMd: z.string(),
  rationaleMd: z.string(),
  review: z.record(z.string(), ReviewItem),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
  acceptedAt: isoDateTime.nullable(),
  reviewedAt: isoDateTime.nullable(),
})
export type DailyPlan = z.infer<typeof DailyPlan>

export const CalendarStatus = z.object({
  connected: z.boolean(),
  accountEmail: z.string().nullable(),
  lastSyncedAt: isoDateTime.nullable(),
  error: z.string().nullable(),
  /** server has a Google OAuth client configured */
  available: z.boolean(),
})
export type CalendarStatus = z.infer<typeof CalendarStatus>

export const TodayView = z.object({
  date: isoDate,
  plan: DailyPlan.nullable(),
  events: z.array(CalendarEvent),
  focusTasks: z.array(TaskFull),
  overdue: z.array(TaskFull),
  commitmentsDue: z.array(Commitment),
  inboxPending: z.number().int(),
  calendar: CalendarStatus,
  /** evening review is due (after the configured time, plan accepted, not yet reviewed) */
  reviewDue: z.boolean(),
})
export type TodayView = z.infer<typeof TodayView>

export const UpdatePlan = z.object({
  /** new times for moved blocks (pinned afterwards) */
  moved: z.array(z.object({ id: z.string(), start: isoDateTime, end: isoDateTime })).default([]),
  removed: z.array(z.string()).default([]),
})
export type UpdatePlan = z.infer<typeof UpdatePlan>

export const AcceptResult = z.object({
  plan: DailyPlan,
  /** ◆ events created/updated/deleted; 0 without a connected calendar */
  written: z.number().int(),
  calendar: z.boolean(),
})
export type AcceptResult = z.infer<typeof AcceptResult>

export const EveningReview = z.object({ items: z.array(ReviewItem).max(200) })
export type EveningReview = z.infer<typeof EveningReview>

export const DeepWorkWindow = z.object({ start: hhmm, end: hhmm })

export const PlanningSettings = z.object({
  /** daily plan job time (§16.2, default weekdays 06:30) */
  planTime: hhmm.default('06:30'),
  /** 0 = Monday … 6 = Sunday */
  planDays: z.array(z.number().int().min(0).max(6)).max(7).default([0, 1, 2, 3, 4]),
  workdayStart: hhmm.default('08:30'),
  workdayEnd: hhmm.default('17:30'),
  /** energy profile: deep work windows */
  deepWork: z
    .array(DeepWorkWindow)
    .max(4)
    .default([{ start: '08:30', end: '11:30' }]),
  bufferPct: z.number().int().min(0).max(60).default(20),
  /** prep/follow-up block before/after external meetings */
  prepMin: z.number().int().min(0).max(60).default(10),
  /** new, moved or cancelled meetings re-arrange colliding plan blocks automatically (ADR 0016) */
  autoReplan: z.boolean().default(true),
  /** evening review push; null = off */
  eveningReviewTime: hhmm.nullable().default('17:30'),
  notify: z
    .object({
      planReady: z.boolean().default(true),
      upcomingEvents: z.boolean().default(true),
      eveningReview: z.boolean().default(true),
      /** minutes before an event */
      leadMin: z.number().int().min(1).max(60).default(10),
    })
    .default({ planReady: true, upcomingEvents: true, eveningReview: true, leadMin: 10 }),
})
export type PlanningSettings = z.infer<typeof PlanningSettings>
export const UpdatePlanningSettings = patchOf(
  PlanningSettings.extend({ notify: patchOf(PlanningSettings.shape.notify.unwrap()) }),
)
export type UpdatePlanningSettings = z.infer<typeof UpdatePlanningSettings>

export const CalendarInfo = z.object({
  id: z.string(),
  summary: z.string(),
  primary: z.boolean(),
  /** synced into "Heute" (busy times) */
  selected: z.boolean(),
})
export type CalendarInfo = z.infer<typeof CalendarInfo>

export const CalendarAccountInfo = z.object({
  id,
  provider: z.enum(['google', 'microsoft']),
  accountEmail: z.string(),
  status: z.enum(['active', 'error']),
  error: z.string().nullable(),
  lastSyncedAt: isoDateTime.nullable(),
  calendars: z.array(CalendarInfo),
  /** where accepted ◆ blocks are written */
  defaultCalendarId: z.string().nullable(),
})
export type CalendarAccountInfo = z.infer<typeof CalendarAccountInfo>

export const UpdateCalendarAccount = z.object({
  selected: z.array(z.string()).max(50).optional(),
  defaultCalendarId: z.string().max(500).optional(),
})
export type UpdateCalendarAccount = z.infer<typeof UpdateCalendarAccount>
